import datasetStorage from './datasetStorage';
import queryHistoryStorage from './queryHistoryStorage';

const BUNDLE_VERSION = 1;

const normalizeFilename = (value) => String(value || 'dataset')
  .replace(/\s+/g, '_')
  .replace(/[^a-zA-Z0-9_-]/g, '')
  .toLowerCase();

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const serializeDataset = (dataset) => {
  if (dataset.data_type === 'binary' && dataset.data) {
    const buffer = dataset.data instanceof ArrayBuffer
      ? dataset.data
      : dataset.data.buffer;
    return {
      data: arrayBufferToBase64(buffer),
      data_encoding: 'base64'
    };
  }

  return {
    data: dataset.data,
    data_encoding: 'none'
  };
};

const deserializeDataset = (dataset) => {
  if (dataset.data_type === 'binary' && dataset.data_encoding === 'base64') {
    return {
      ...dataset,
      data: base64ToArrayBuffer(dataset.data)
    };
  }
  return dataset;
};

const createBundle = async ({ fileId }) => {
  const dataset = await datasetStorage.loadDatasetById(fileId);
  if (!dataset) {
    throw new Error('Dataset not found in local storage');
  }

  const queries = await queryHistoryStorage.loadQueriesByDataset(fileId);
  const serialized = serializeDataset(dataset);

  return {
    version: BUNDLE_VERSION,
    exported_at: new Date().toISOString(),
    dataset: {
      file_id: dataset.file_id,
      filename: dataset.filename,
      file_extension: dataset.file_extension,
      data_type: dataset.data_type,
      data: serialized.data,
      data_encoding: serialized.data_encoding,
      columns: dataset.columns,
      row_count: dataset.row_count,
      file_size_bytes: dataset.file_size_bytes,
      upload_time: dataset.upload_time,
      created_at: dataset.created_at
    },
    queries: queries.map((query) => ({
      id: query.id,
      question: query.question,
      sql: query.sql,
      results: query.results,
      row_count: query.row_count,
      execution_time_ms: query.execution_time_ms,
      created_at: query.created_at,
      saved: query.saved
    }))
  };
};

const downloadBundle = (bundle, filename) => {
  const content = JSON.stringify(bundle, null, 2);
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.duckpilot.json`;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 100);
};

const exportBundle = async ({ fileId }) => {
  const bundle = await createBundle({ fileId });
  const filename = normalizeFilename(bundle.dataset.filename);
  downloadBundle(bundle, filename);
};

const ensureUniqueFileId = async (fileId) => {
  let nextId = fileId;
  let counter = 1;
  while (await datasetStorage.loadDatasetById(nextId)) {
    nextId = `${fileId}-${counter}`;
    counter += 1;
  }
  return nextId;
};

const importBundle = async (bundle) => {
  if (!bundle?.dataset?.file_id) {
    throw new Error('Invalid bundle format');
  }

  const fileId = await ensureUniqueFileId(bundle.dataset.file_id);
  const dataset = deserializeDataset({
    ...bundle.dataset,
    file_id: fileId
  });

  await datasetStorage.saveDataset(dataset);

  const queries = bundle.queries || [];
  for (const query of queries) {
    await queryHistoryStorage.saveQuery({
      ...query,
      dataset_id: fileId,
      dataset_name: bundle.dataset.filename
    });
  }

  return dataset;
};

const bundleService = {
  exportBundle,
  importBundle
};

export default bundleService;
